import { useEffect, useState } from 'react';
import {
  WORD_LENGTH, createGame, dailyAnswer, dailyLength, loadLength,
  type GameState, type Language,
} from '@lexicon/core';
import { DailyBanner } from '../components/DailyBanner';
import { ChevronRightIcon } from '../components/icons';
import { StatStrip } from '../components/StatStrip';
import { useLengthValidator } from '../game/useLengthValidator';
import { loadSession, toGameState, type GameResult } from '../game/session';
import { navigate } from '../lib/router';
import { useApp } from '../state/AppContext';
import { GamePlay } from './GamePlay';

interface Boot {
  /** The day this boot was loaded for — see the render guard below. */
  dayNumber: number;
  /** The language it was loaded for; a switch invalidates it exactly as a rollover does. */
  language: Language;
  game: GameState;
  result: GameResult | null;
  revealedInsights: string[];
}

/** How long after boot to fetch tomorrow's guess list. */
const NEXT_DAY_PREFETCH_MS = 3000;

export function DailyScreen() {
  const { ready, storage, today, dayNumber, profile, stats, language, t } = useApp();
  const [boot, setBoot] = useState<Boot | null>(null);

  // `today` is AppContext state and is re-derived on a day rollover, so
  // the ladder length follows the rollover with no extra machinery. The
  // ladder is language-neutral (daily.ts) — only the pool the answer comes
  // from differs.
  const length = dailyLength(today);
  const { isValid, retry } = useLengthValidator(length, language);

  // Tomorrow's list, once the app has settled. The daily's ANSWERS are all
  // in the main bundle (spec §10.2), so this is the difference between a
  // player who opens the app offline tomorrow being able to submit a guess
  // and not. Skipped when tomorrow needs nothing new.
  useEffect(() => {
    const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    const next = dailyLength(tomorrow);
    // Only English length 5 is in the main bundle; every Dutch length is a
    // chunk, so for Dutch even a 5-letter tomorrow is worth prefetching.
    if (next === length || (language === 'en' && next === WORD_LENGTH)) return undefined;
    const timer = window.setTimeout(
      () => { void loadLength(next, language).catch(() => {}); },
      NEXT_DAY_PREFETCH_MS,
    );
    return () => window.clearTimeout(timer);
  }, [today, length, language]);

  useEffect(() => {
    // I1 follow-up (critical): a rollover mid-session changes `dayNumber`
    // while this effect's load is still in flight, or hasn't started yet.
    // Clearing `boot` synchronously — before the (re)load — means the very
    // next render already has no boot to show; combined with the
    // `boot.dayNumber !== dayNumber` guard below, GamePlay can never mount
    // with one day's already-finished game under another day's id. Without
    // this, the render that reacts to the new `dayNumber` would still hand
    // GamePlay the *previous* day's finished `boot.game` (stale state) as
    // `initial`/`initialResult` under the *new* day's id — and because
    // useGame fires `onFinished` for an already-finished `initial` on
    // mount, that silently re-finalized the old game as if it belonged to
    // the new day: double-awarded XP/streak, and overwrote the stored
    // dailyGame session with the wrong id/answer, locking the new day's
    // real puzzle out and spoiling its answer in the still-open result
    // sheet.
    setBoot(null);
    if (!ready) return undefined;
    let cancelled = false;
    const bootDayNumber = dayNumber;
    void (async () => {
      try {
        const session = await loadSession(storage, 'daily', String(bootDayNumber), language);
        if (cancelled) return;
        const game = session
          ? toGameState(session)
          : createGame(dailyAnswer(today, language), 'daily');
        setBoot({
          dayNumber: bootDayNumber,
          language,
          game,
          result: session?.result ?? null,
          revealedInsights: session?.revealedInsights ?? [],
        });
      } catch {
        // A broken storage read here (spec §7: never crash) must not leave
        // the screen stuck on "Loading…" forever — fall back to a fresh,
        // unsaved daily exactly as if no session had been found.
        if (cancelled) return;
        setBoot({
          dayNumber: bootDayNumber,
          language,
          game: createGame(dailyAnswer(today, language), 'daily'),
          result: null,
          revealedInsights: [],
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, storage, today, dayNumber, language]);

  // Belt-and-braces alongside the effect's synchronous `setBoot(null)`:
  // even if some future change made that reset arrive a render late, this
  // guard alone is enough — it is evaluated during the same render that
  // first sees the new `dayNumber`, before GamePlay could ever mount with
  // mismatched initial props.
  if (!boot || boot.dayNumber !== dayNumber || boot.language !== language) {
    return <p className="p-6 text-center text-dim">{t.daily.loading}</p>;
  }

  return (
    <GamePlay
      // Keyed by language too: switching mid-game is a different puzzle,
      // and GamePlay must remount rather than re-render onto it.
      key={`${language}:${dayNumber}`}
      id={String(dayNumber)}
      initial={boot.game}
      initialResult={boot.result}
      initialRevealedInsights={boot.revealedInsights}
      dayNumber={dayNumber}
      isValid={isValid}
      onValidationPending={retry}
      shareId={dayNumber}
      ctaLabel={t.daily.challengeAFriend}
      onCta={() => navigate('/challenge/new')}
      banner={({ finished, openResult }) => (
        <DailyBanner
          date={today}
          length={length}
          wordListPending={isValid === null}
          action={finished ? (
            <button
              type="button"
              className="flex min-h-[44px] items-center gap-1 whitespace-nowrap text-sm text-dim"
              onClick={openResult}
            >
              {t.common.viewResult}
              <ChevronRightIcon className="h-4 w-4 shrink-0" />
            </button>
          ) : null}
        >
          <StatStrip streak={profile.streak.currentStreak} wins={stats.won} xp={profile.totalXp} />
        </DailyBanner>
      )}
    />
  );
}
